/**
 * SmartInput — a single, app-wide, additive suggestion layer for every text field.
 *
 * Mounted once at the app root. It listens (via event delegation) to focus/typing on
 * any text input or textarea and shows a small popover under the field with:
 *   • context autocomplete (categories, suppliers, payment methods, vehicle makes)
 *   • format fixes (UK postcode / phone / reg plate, email lower-casing + domain typos)
 *   • capitalisation suggestions (Title Case for names, Sentence case for notes)
 *   • light spelling fixes for common typos
 *
 * Suggestions are ALWAYS tap-to-accept — nothing is ever changed silently. Code and
 * money fields (SKU, barcode, VIN, prices, quantities) get safe keyboard/spellcheck
 * attributes only, never value corrections. The layer is purely additive: normal
 * typing flows straight through the field's own onChange untouched, so it cannot
 * break form behaviour even if a suggestion is wrong.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "./api";

type El = HTMLInputElement | HTMLTextAreaElement;
type Sug = { label: string; value: string; tag: string };

// ── curated vehicle makes (scooters / motorcycles) for make/brand fields ──
const MAKES = [
  "Honda", "Yamaha", "Piaggio", "Vespa", "Suzuki", "Kawasaki", "Aprilia", "Peugeot",
  "Sym", "Kymco", "Lexmoto", "Lambretta", "Gilera", "Derbi", "Keeway", "Lexus",
  "BMW", "Triumph", "Ducati", "KTM", "Royal Enfield", "Harley-Davidson", "Husqvarna",
  "Moto Guzzi", "MV Agusta", "Benelli", "CFMoto", "Sinnis", "Direct Bikes", "Niu",
  "Super Soco", "Zontes", "Mash", "Mutt", "Fantic", "Beta", "Sherco", "Indian",
];

// ── common email domain typos ──
const DOMAIN_FIX: Record<string, string> = {
  "gmial.com": "gmail.com", "gmai.com": "gmail.com", "gamil.com": "gmail.com",
  "gmal.com": "gmail.com", "gnail.com": "gmail.com", "gmail.co": "gmail.com",
  "hotmial.com": "hotmail.com", "hotmal.com": "hotmail.com", "hotnail.com": "hotmail.com",
  "yaho.com": "yahoo.com", "yahooo.com": "yahoo.com", "yhaoo.com": "yahoo.com",
  "outlok.com": "outlook.com", "outloo.com": "outlook.com", "hotmail.co": "hotmail.com",
  "iclould.com": "icloud.com", "iclod.com": "icloud.com", "live.co": "live.com",
};

// ── small spelling dictionary (whole-word, case-preserving) ──
const TYPOS: Record<string, string> = {
  recieve: "receive", reciept: "receipt", seperate: "separate", definately: "definitely",
  occured: "occurred", untill: "until", accomodation: "accommodation", calender: "calendar",
  adress: "address", arguement: "argument", buisness: "business", goverment: "government",
  maintainance: "maintenance", neccessary: "necessary", occassion: "occasion",
  paymnet: "payment", quantitiy: "quantity", refered: "referred", succesful: "successful",
  tommorow: "tomorrow", vehical: "vehicle", warrenty: "warranty", milage: "mileage",
};

function setNativeValue(el: El, value: string) {
  const proto = el instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  setter ? setter.call(el, value) : (el.value = value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

function labelText(el: El): string {
  let s = "";
  try {
    if (el.id) { const l = document.querySelector(`label[for="${CSS.escape(el.id)}"]`); if (l) s += " " + (l.textContent || ""); }
    const wrap = el.closest("label"); if (wrap) s += " " + (wrap.textContent || "");
  } catch { /* ignore */ }
  return s;
}

type Kind = "email" | "phone" | "postcode" | "reg" | "code" | "number"
  | "category" | "supplier" | "method" | "make" | "name" | "sentence" | "text" | null;

function inferKind(el: El): Kind {
  const type = (el.getAttribute("type") || "text").toLowerCase();
  if (el.disabled || (el as HTMLInputElement).readOnly) return null;
  if (el.getAttribute("data-smart") === "off") return null;
  if (["password", "search", "hidden", "file", "checkbox", "radio", "range", "color", "date", "time", "datetime-local"].includes(type)) return null;
  const im = (el.getAttribute("inputmode") || "").toLowerCase();
  const hay = `${labelText(el)} ${el.getAttribute("name") || ""} ${el.id} ${el.getAttribute("placeholder") || ""} ${el.getAttribute("aria-label") || ""}`.toLowerCase();
  if (/search|filter|query/.test(hay)) return null;
  if (type === "email" || /e-?mail/.test(hay)) return "email";
  if (type === "tel" || /phone|mobile|\btel\b|contact number|whatsapp/.test(hay)) return "phone";
  if (/post ?code|postcode|\bzip\b/.test(hay)) return "postcode";
  if (/\breg\b|registration|number ?plate|\bplate\b|\bvrm\b/.test(hay)) return "reg";
  if (/\bsku\b|barcode|\bvin\b|\bimei\b|\bcode\b|reference|invoice no|\bref\b/.test(hay)) return "code";
  if (type === "number" || im === "numeric" || im === "decimal"
      || /price|cost|amount|\bqty\b|quantity|\bvat\b|discount|total|balance|\brate\b|mileage|\byear\b|stock|deposit|odometer|\bcc\b/.test(hay)) return "number";
  if (/categor/.test(hay)) return "category";
  if (/supplier|payee|vendor/.test(hay)) return "supplier";
  if (/payment method|\bmethod\b/.test(hay)) return "method";
  if (/\bmake\b|manufacturer|\bbrand\b/.test(hay)) return "make";
  if (el.tagName === "TEXTAREA" || /notes?|description|comment|message|remarks?|details/.test(hay)) return "sentence";
  if (/address|street|line ?[12]/.test(hay)) return "sentence";
  if (/name|customer|\btitle\b|model|colou?r|town|city|company|legal/.test(hay)) return "name";
  return "text";
}

function applyAttrs(el: El, kind: Kind) {
  const has = (a: string) => el.hasAttribute(a);
  const set = (a: string, v: string) => { if (!has(a)) el.setAttribute(a, v); };
  const prose = kind === "name" || kind === "sentence" || kind === "text";
  set("spellcheck", prose ? "true" : "false");
  if (kind === "name") set("autocapitalize", "words");
  else if (prose) set("autocapitalize", "sentences");
  else if (kind === "reg" || kind === "postcode") set("autocapitalize", "characters");
  else set("autocapitalize", "off");
  if (kind === "email") set("inputmode", "email");
  else if (kind === "phone") set("inputmode", "tel");
  else if (kind === "number") set("inputmode", "decimal");
}

// ── formatters ──
function titleCase(v: string) { return v.replace(/(^|[\s'’\-/])([a-z])/g, (_m, p, c) => p + c.toUpperCase()); }
function sentenceCase(v: string) { return v.replace(/(^\s*[a-z])|([.!?]\s+[a-z])/g, (m) => m.toUpperCase()); }
function fixDomain(email: string): string | null {
  const at = email.lastIndexOf("@"); if (at < 0) return null;
  const local = email.slice(0, at), dom = email.slice(at + 1).toLowerCase();
  let nd = DOMAIN_FIX[dom] || null;
  if (!nd) { if (/\.(con|cm|comm|cmo)$/.test(dom)) nd = dom.replace(/\.(con|cm|comm|cmo)$/, ".com"); }
  return nd ? `${local}@${nd}` : null;
}
function fmtPhone(s: string): string | null {
  let d = s.replace(/[^\d+]/g, "");
  if (d.startsWith("+44")) d = "0" + d.slice(3);
  d = d.replace(/\D/g, "");
  if (d.length !== 11 || d[0] !== "0") return null;
  let out = d;
  if (d.startsWith("07")) out = d.replace(/(\d{5})(\d{6})/, "$1 $2");
  else if (d.startsWith("020")) out = d.replace(/(\d{3})(\d{4})(\d{4})/, "$1 $2 $3");
  else out = d.replace(/(\d{4})(\d{3})(\d{4})/, "$1 $2 $3");
  return out !== s ? out : null;
}
function fmtPostcode(s: string): string | null {
  const t = s.toUpperCase().replace(/\s+/g, "");
  if (!/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(t)) return null;
  const f = t.slice(0, -3) + " " + t.slice(-3);
  return f !== s ? f : null;
}
function fmtReg(s: string): string | null {
  const up = s.toUpperCase(), t = up.replace(/\s+/g, "");
  if (/^[A-Z]{2}\d{2}[A-Z]{3}$/.test(t)) { const f = t.slice(0, 4) + " " + t.slice(4); return f !== s ? f : null; }
  return up !== s ? up : null;
}
function fixTypos(v: string): string | null {
  let hit = false;
  const out = v.replace(/[A-Za-z]+/g, (w) => {
    const r = TYPOS[w.toLowerCase()];
    if (!r) return w;
    hit = true;
    return w[0] === w[0].toUpperCase() ? r[0].toUpperCase() + r.slice(1) : r;
  });
  return hit && out !== v ? out : null;
}

type Data = { category: string[]; supplier: string[]; method: string[]; make: string[] };

function autocomplete(list: string[], v: string): Sug[] {
  const q = v.trim().toLowerCase();
  if (!q) return [];
  const starts: string[] = [], contains: string[] = [];
  for (const item of list) {
    const il = item.toLowerCase();
    if (il === q) continue;
    if (il.startsWith(q)) starts.push(item);
    else if (il.includes(q)) contains.push(item);
  }
  return [...starts, ...contains].slice(0, 6).map((value) => ({ label: value, value, tag: "use" }));
}

function buildSuggestions(kind: Kind, value: string, data: Data): Sug[] {
  const v = value;
  const out: Sug[] = [];
  const push = (s: Sug | null) => { if (s && s.value !== v && !out.some((o) => o.value === s.value)) out.push(s); };
  switch (kind) {
    case "category": return autocomplete(data.category, v);
    case "supplier": return autocomplete(data.supplier, v);
    case "method": return autocomplete(data.method, v);
    case "make": return autocomplete(data.make, v);
    case "email": {
      const t = v.trim();
      const lower = t.toLowerCase();
      if (lower !== t) push({ label: lower, value: lower, tag: "lower-case" });
      const fixed = fixDomain(lower); if (fixed) push({ label: fixed, value: fixed, tag: "did you mean" });
      return out;
    }
    case "phone": { const f = fmtPhone(v); push(f ? { label: f, value: f, tag: "format" } : null); return out; }
    case "postcode": { const f = fmtPostcode(v); push(f ? { label: f, value: f, tag: "format" } : null); return out; }
    case "reg": { const f = fmtReg(v); push(f ? { label: f, value: f, tag: "format" } : null); return out; }
    case "name": {
      if (!/[A-Z]/.test(v)) { const tc = titleCase(v); push(tc !== v ? { label: tc, value: tc, tag: "capitalise" } : null); }
      const sp = fixTypos(v); push(sp ? { label: sp, value: sp, tag: "spelling" } : null);
      return out;
    }
    case "sentence": case "text": {
      if (!/[A-Z]/.test(v)) { const sc = sentenceCase(v); push(sc !== v ? { label: sc, value: sc, tag: "capitalise" } : null); }
      const sp = fixTypos(v); push(sp ? { label: sp, value: sp, tag: "spelling" } : null);
      return out;
    }
    default: return out; // code / number: attributes only
  }
}

export default function SmartInput() {
  const [sugs, setSugs] = useState<Sug[]>([]);
  const [rect, setRect] = useState<{ left: number; top: number; bottom: number; width: number } | null>(null);
  const elRef = useRef<El | null>(null);
  const kindRef = useRef<Kind>(null);
  const composing = useRef(false);
  const data = useRef<Data>({ category: [], supplier: [], method: [], make: MAKES });
  const loaded = useRef(false);

  const loadData = useCallback(async () => {
    if (loaded.current) return; loaded.current = true;
    try { const m: any = await api.get("/products/meta"); data.current.category = uniq([...data.current.category, ...((m.categories || []).map((c: any) => c.name))]); } catch { /* perms */ }
    try { const e: any = await api.get("/expenses/meta"); data.current.category = uniq([...data.current.category, ...(e.categories || []), ...(e.used_categories || [])]); data.current.method = uniq(e.methods || []); } catch { /* perms */ }
    try { const s: any = await api.get("/purchasing/suppliers"); data.current.supplier = uniq((s.suppliers || []).map((x: any) => x.name).filter(Boolean)); } catch { /* perms */ }
  }, []);

  const place = useCallback((el: El) => {
    const r = el.getBoundingClientRect();
    setRect({ left: r.left, top: r.top, bottom: r.bottom, width: r.width });
  }, []);

  const refresh = useCallback((el: El) => {
    try {
      const kind = kindRef.current;
      const next = buildSuggestions(kind, el.value || "", data.current);
      setSugs(next);
      if (next.length) place(el); else setRect(null);
    } catch { setSugs([]); setRect(null); }
  }, [place]);

  const close = useCallback(() => { elRef.current = null; setSugs([]); setRect(null); }, []);

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as El;
      if (!el || (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA")) return;
      const kind = inferKind(el);
      if (!kind) { close(); return; }
      void loadData();
      try { applyAttrs(el, kind); } catch { /* ignore */ }
      elRef.current = el; kindRef.current = kind;
      refresh(el);
    };
    const onInput = (e: Event) => {
      const el = e.target as El;
      if (el !== elRef.current || composing.current) return;
      refresh(el);
    };
    const onFocusOut = (e: FocusEvent) => { if (e.target === elRef.current) close(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    const onScroll = () => { if (elRef.current && sugsRef.current.length) place(elRef.current); };
    const onCompStart = () => { composing.current = true; };
    const onCompEnd = (e: Event) => { composing.current = false; if (e.target === elRef.current) refresh(e.target as El); };

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("input", onInput, true);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("keydown", onKey);
    document.addEventListener("compositionstart", onCompStart, true);
    document.addEventListener("compositionend", onCompEnd, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("compositionstart", onCompStart, true);
      document.removeEventListener("compositionend", onCompEnd, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [refresh, close, place, loadData]);

  // keep latest sugs for the scroll handler without re-binding listeners
  const sugsRef = useRef<Sug[]>([]);
  useEffect(() => { sugsRef.current = sugs; }, [sugs]);

  const accept = (s: Sug) => {
    const el = elRef.current; if (!el) return;
    setNativeValue(el, s.value);
    el.focus();
    requestAnimationFrame(() => { if (elRef.current) refresh(elRef.current); });
  };

  if (!rect || !sugs.length) return null;

  const POP_W = Math.max(rect.width, 220);
  const left = Math.min(rect.left, window.innerWidth - POP_W - 10);
  const below = rect.bottom + 4;
  const openUp = below + 44 + sugs.length * 38 > window.innerHeight;
  const top = openUp ? Math.max(8, rect.top - 6 - (8 + sugs.length * 38)) : below;

  return createPortal(
    <div
      role="listbox"
      onPointerDown={(e) => e.preventDefault()} /* keep focus on the field */
      style={{
        position: "fixed", left, top, width: POP_W, zIndex: 100000,
        background: "var(--card, #fff)", border: "1px solid var(--line, #d9d9d9)",
        borderRadius: 4, boxShadow: "0 8px 24px rgba(0,0,0,.16)", overflow: "hidden",
        fontFamily: "inherit", maxHeight: "min(50vh, 320px)", overflowY: "auto",
      }}
    >
      {sugs.map((s, i) => (
        <button
          key={`${s.value}-${i}`}
          type="button"
          onPointerDown={(e) => { e.preventDefault(); accept(s); }}
          style={{
            display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left",
            padding: "9px 12px", border: "none", borderTop: i ? "1px solid var(--line, #eee)" : "none",
            background: "transparent", cursor: "pointer", fontSize: 14, color: "var(--ink, #111)",
          }}
        >
          <span style={{
            fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".02em",
            color: "var(--muted, #888)", border: "1px solid var(--line, #ddd)", borderRadius: 4,
            padding: "1px 5px", flexShrink: 0, whiteSpace: "nowrap",
          }}>{s.tag}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{s.label}</span>
        </button>
      ))}
    </div>,
    document.body,
  );
}

function uniq(a: string[]): string[] { return Array.from(new Set(a.map((s) => (s || "").trim()).filter(Boolean))); }
